import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
var _dec, _class, _ContentEditable;
function _callSuper(t, o, e) { return o = _getPrototypeOf(o), _possibleConstructorReturn(t, _isNativeReflectConstruct() ? Reflect.construct(o, e || [], _getPrototypeOf(t).constructor) : o.apply(t, e)); }
function _isNativeReflectConstruct() { try { var t = !Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); } catch (t) {} return (_isNativeReflectConstruct = function _isNativeReflectConstruct() { return !!t; })(); }
/** @jsx jsx */
// react-compatible contenteditable component inspiration from
// http://stackoverflow.com/questions/22677931/react-js-onchange-event-for-contenteditable

import { Component } from 'react';
import PropTypes from 'prop-types';
import pick from 'lodash/fp/pick';
import platform from 'platform';
import { View } from '@instructure/ui-view';
import { jsx } from '@instructure/emotion';
import generateComponentTheme from './theme';
import generateStyle from './styles';
import { withStyleOverrides } from '@instructure/quiz-common';
var BACKSPACE_KEY = 'Backspace';
var ENTER_KEY = 'Enter';
var KEYS = ['b', 'i', 'u'];
function shouldStopEffect(e) {
  return (e.metaKey || e.ctrlKey) && KEYS.includes(e.key);
}
var ContentEditable = (_dec = withStyleOverrides(generateStyle, generateComponentTheme), _dec(_class = (_ContentEditable = /*#__PURE__*/function (_Component) {
  function ContentEditable() {
    var _this;
    _classCallCheck(this, ContentEditable);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _callSuper(this, ContentEditable, [].concat(args));
    _defineProperty(_this, "span", null);
    _defineProperty(_this, "handleKeyPress", function (e) {
      // prevent <br> from being created in contentEditable
      if (e.key === ENTER_KEY) {
        e.preventDefault();
      }
      _this.props.onKeyDown(e);
    });
    _defineProperty(_this, "handleKeyUp", function (e) {
      if (_this.props.enableFormatting) {
        return;
      }
      if (shouldStopEffect(e)) {
        e.preventDefault();
        e.stopPropagation();
      }

      // IE11 spans don't trigger onInput on every keystroke, so use
      // keyUp here instead
      if (platform.name === 'IE' && platform.version === '11.0') {
        _this.handleInput(e);
      }
    });
    // For some reason MS Edge 15 does not let us use backspace to delete the
    // first character in the span. This horrible hack listens for keyDown
    // events, and removes the first character in the span if backspace is
    // pressed when the caret is directly after it.
    // TODO: remove this when MS Edge gets its act together
    _defineProperty(_this, "handleKeyDown", function (e) {
      if (e.key !== BACKSPACE_KEY) return;
      var selection = window.getSelection();
      if (selection.rangeCount !== 1) return;
      var range = selection.getRangeAt(0);
      if (range.commonAncestorContainer.parentNode === _this.span && range.startOffset === 1 && range.endOffset === 1) {
        e.preventDefault();
        _this.span.textContent = _this.span.textContent.slice(1);
        _this.handleInput(e);
      }
    });
    _defineProperty(_this, "handleInput", function (e) {
      _this.props.onChange(e);
      _this.props.onInput(e);
    });
    _defineProperty(_this, "handleBlur", function (e) {
      _this.props.onChange(e);
      _this.props.onBlur(e);
    });
    _defineProperty(_this, "handleFocus", function (e) {
      _this.props.onChange(e);
      _this.props.onFocus(e);
    });
    _defineProperty(_this, "handleRef", function (node) {
      _this.span = node;
    });
    return _this;
  }
  _inherits(ContentEditable, _Component);
  return _createClass(ContentEditable, [{
    key: "shouldComponentUpdate",
    value:
    // see the stackoverflow question cited at the top of this file for
    // an explanation of why the content is managed through lifecylce functions
    // instead of just rendering with dangerouslySetInnerHTML (or as children).
    function shouldComponentUpdate(nextProps) {
      return nextProps.content !== this.span.textContent;
    }
  }, {
    key: "componentDidMount",
    value: function componentDidMount() {
      this.span.textContent = this.props.content;
    }
  }, {
    key: "componentDidUpdate",
    value: function componentDidUpdate() {
      if (this.props.content !== this.span.textContent) {
        this.span.textContent = this.props.content;
      }
    }
  }, {
    key: "focusAndSetCursorOnPosition",
    value: function focusAndSetCursorOnPosition(position) {
      if (!this.span) {
        return;
      }
      this.span.focus();
      var range = document.createRange();
      var sel = window.getSelection();
      var offset = 0;
      this.span.childNodes.forEach(function (node) {
        if (!node.length || position - offset > node.length) {
          offset += node.length || 1;
        } else {
          range.setStart(node, 0);
          range.setEnd(node, position - offset);
          sel.removeAllRanges();
          sel.addRange(range);
          sel.collapseToEnd();
        }
      });
    }
  }, {
    key: "render",
    value: function render() {
      var props = pick(['id', 'onMouseUp', 'onKeyUp', 'onPaste', 'placeholder'], this.props);
      var labelId = "ce-label-for-".concat(this.props.id);
      return jsx(View, null, jsx("label", {
        id: labelId,
        htmlFor: this.props.id
      }, this.props.label), jsx("span", Object.assign({
        "aria-labelledby": labelId
      }, props, {
        css: [this.props.styles.root, this.props.customStyles],
        ref: this.handleRef,
        onInput: this.handleInput,
        onBlur: this.handleBlur,
        onFocus: this.handleFocus,
        onKeyPress: this.handleKeyPress,
        onKeyUp: this.handleKeyUp,
        onKeyDown: this.handleKeyDown,
        role: "textbox",
        tabIndex: "0",
        contentEditable: !this.props.disabled
      })));
    }
  }]);
}(Component), _defineProperty(_ContentEditable, "displayName", 'ContentEditable'), _defineProperty(_ContentEditable, "componentId", "Quizzes".concat(_ContentEditable.displayName)), _defineProperty(_ContentEditable, "propTypes", {
  label: PropTypes.node.isRequired,
  customStyles: PropTypes.object,
  disabled: PropTypes.bool,
  enableFormatting: PropTypes.bool,
  content: PropTypes.string,
  id: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  onInput: PropTypes.func,
  onKeyDown: PropTypes.func,
  onKeyUp: PropTypes.func,
  onMouseUp: PropTypes.func,
  onPaste: PropTypes.func,
  placeholder: PropTypes.string,
  styles: PropTypes.object
}), _defineProperty(_ContentEditable, "defaultProps", {
  customStyles: {},
  disabled: false,
  enableFormatting: true,
  onBlur: Function.prototype,
  onFocus: Function.prototype,
  onInput: Function.prototype,
  onKeyDown: Function.prototype,
  content: void 0,
  id: void 0,
  onKeyUp: void 0,
  onMouseUp: void 0,
  onPaste: void 0,
  placeholder: void 0
}), _ContentEditable)) || _class);
export { ContentEditable as default };